import { useState, useEffect } from 'react';
import { InfiniteData, UseInfiniteQueryResult, UseQueryOptions, useQuery, useQueryClient } from 'react-query';
import type { StreamChat, MessageFilters } from 'stream-chat';
import { dateCompare } from '@frontend/date';
import {
  useInfiniteQueryWithSetter,
  useQueryWithSetter,
  ContextlessQueryObserverOptions,
} from '@frontend/react-query-helpers';
import { Message, User, QueryConversationsResponse, KeywordSearchQueryParams } from '../types';
import {
  getUnreadMentionsCount,
  getMentions,
  initializeStreamClient,
  getUsers,
  getConversations,
  getConversationMessages,
  getReplies,
  getThreads,
  getThreadsUnreadCount,
  getUsersWithParams,
  searchMessages,
} from './stream-api';
import {
  USERS_QUERY_LIMIT,
  THREADS_QUERY_LIMIT,
  USERS_QUERY_STALE_TIME,
  MESSAGE_SEARCH_LIMIT,
  MESSAGE_SEARCH_QUERY_STALE_TIME,
} from './stream-constants';
import { isUsersMapEqual, logStreamErrorToSentry } from './stream-utils';

type ClientWithUser = StreamChat & { user: NonNullable<StreamChat['user']> };
type QueryOptionsWithOnError<TData = unknown> = ContextlessQueryObserverOptions & {
  onError?: (error: unknown, ...args: unknown[]) => void;
  onSuccess?: (data: TData) => void;
};
const verifyClientWithUser = (client: StreamChat | undefined) => {
  if (!client) {
    throw new Error('Stream client is not initialized');
  }
  if (!client.user?.id) {
    throw new Error('Stream client user is not connected');
  }
  return Promise.resolve(client as ClientWithUser);
};

const BaseKey = 'team-chat';
const threadsBaseKeys = [BaseKey, 'threads'];
const messageByKeywordBaseKey = 'messages-by-keyword';

export const queryKeys = {
  token: (orgId: string) => [BaseKey, orgId],
  client: (orgId: string, userId: string) => [BaseKey, 'client', orgId, userId],
  unreadMentionsCount: (userId: string) => [BaseKey, userId, 'unread-mentions-count'],
  mentions: (userId: string, limit: number, offset: number) => [BaseKey, userId, 'mentions', limit, offset],
  users: (orgId: string) => [BaseKey, orgId, 'users'],
  conversations: (userId: string) => [BaseKey, userId, 'conversations'],
  messages: (conversationId: string, aroundMessageId?: string) => [
    BaseKey,
    conversationId,
    'messages',
    aroundMessageId,
  ],
  threads: (threadId: string) => [...threadsBaseKeys, threadId],
  threadsList: (orgId: string) => [...threadsBaseKeys, orgId, 'threads-list'],
  getUsersByName: (name: string) => [BaseKey, 'get-users-by-name', name],
  messagesByKeyword: (orgId: string, query: KeywordSearchQueryParams) => [
    BaseKey,
    messageByKeywordBaseKey,
    orgId,
    query,
  ],
};

export const useInvalidateAll = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries([BaseKey]);
  };
};

export const useQueryCache = () => {
  const queryClient = useQueryClient();
  const setQueryData = queryClient.setQueryData;
  return { setQueryData };
};

export const useQueryStreamClient = (
  {
    orgId,
    weaveUserId,
  }: {
    orgId: string;
    weaveUserId: string;
  },
  opts: QueryOptionsWithOnError<{ client: StreamChat; user: User }> = {}
) => {
  return useQueryWithSetter({
    queryKey: queryKeys.client(orgId, weaveUserId),
    queryFn: () => initializeStreamClient({ orgId, weaveUserId }),
    retry: false,
    ...opts,
    enabled: opts?.enabled !== false && !!orgId && !!weaveUserId,
    onError: (error, ...args) => {
      logStreamErrorToSentry(error);
      if (opts?.onError) {
        opts.onError(error, ...args);
      }
    },
  });
};

export const useQueryUnreadMentionsCount = (
  streamClient: StreamChat | undefined,
  currentUserId: string,
  opts: QueryOptionsWithOnError<number>
) => {
  return useQueryWithSetter({
    queryKey: queryKeys.unreadMentionsCount(currentUserId),
    queryFn: () => verifyClientWithUser(streamClient).then((client) => getUnreadMentionsCount(client, currentUserId)),
    ...opts,
    enabled: opts?.enabled !== false && !!streamClient?.user?.id,
    onError: (error, ...args) => {
      logStreamErrorToSentry(error);
      if (opts?.onError) {
        opts.onError(error, ...args);
      }
    },
  });
};

export const useQueryMentions = (
  streamClient: StreamChat | undefined,
  currentUserId: string,
  limit: number,
  offset: number,
  opts: QueryOptionsWithOnError<Message[]>
) => {
  return useQueryWithSetter({
    queryKey: queryKeys.mentions(currentUserId, limit, offset),
    queryFn: () =>
      verifyClientWithUser(streamClient).then((client) => getMentions(client, currentUserId, limit, offset)),
    ...opts,
    enabled: opts?.enabled !== false && !!currentUserId && !!streamClient?.user?.id,
    onError: (error, ...args) => {
      logStreamErrorToSentry(error);
      if (opts?.onError) {
        opts.onError(error, ...args);
      }
    },
  });
};

export const useQueryUsers = (
  streamClient: StreamChat | undefined,
  orgId: string,
  opts: QueryOptionsWithOnError<User[]> = {}
) => {
  return useQueryWithSetter({
    queryKey: queryKeys.users(orgId),
    queryFn: () => verifyClientWithUser(streamClient).then((client) => getUsers(client, orgId)),
    ...opts,
    onError: (error, ...args) => {
      logStreamErrorToSentry(error);
      if (opts?.onError) {
        opts.onError(error, ...args);
      }
    },
    enabled: opts?.enabled !== false && !!streamClient?.user?.id,
  });
};

export const useQueryConversations = (
  streamClient: StreamChat | undefined,
  currentUserId: string | undefined,
  orgId: string,
  opts: QueryOptionsWithOnError<QueryConversationsResponse> & { shouldFetchHiddenConversations?: boolean } = {}
) => {
  /* The shouldFetchHiddenConversations is based on the value of feature flag. And since team chat works on org basis 
    we do not need to add this into the query Key. Since it is a temporary thing we are going to remove it after GA */
  const { shouldFetchHiddenConversations, ...queryOpts } = opts;
  const queryKey = queryKeys.conversations(currentUserId ?? 'unknown-user');
  const query = useQueryWithSetter({
    queryKey,
    queryFn: () =>
      verifyClientWithUser(streamClient).then(async (client) => {
        const nonHiddenConversations = await getConversations(client, orgId);
        const hiddenConversations =
          shouldFetchHiddenConversations && (await getConversations(client, orgId, { hidden: true }));

        const dm = [
          ...nonHiddenConversations.dm,
          ...(hiddenConversations && hiddenConversations.dm ? hiddenConversations.dm : []),
        ];
        const groups = [
          ...nonHiddenConversations.groups,
          ...(hiddenConversations && hiddenConversations.groups ? hiddenConversations.groups : []),
        ];

        const unreadDms = dm
          .filter((c) => c.unreadCount > 0)
          .toSorted((c1, c2) => dateCompare(c2.lastMessageSentAt, c1.lastMessageSentAt));
        const readDms = dm
          .filter((c) => c.unreadCount === 0)
          .toSorted((c1, c2) => (!c1.name || !c2.name ? 0 : c1.name.localeCompare(c2.name)));
        const unreadGroups = groups
          .filter((c) => c.unreadCount > 0)
          .toSorted((c1, c2) => dateCompare(c2.lastMessageSentAt, c1.lastMessageSentAt));
        const readGroups = groups
          .filter((c) => c.unreadCount === 0)
          .toSorted((c1, c2) => (!c1.name || !c2.name ? 0 : c1.name.localeCompare(c2.name)));

        return { dm: [...unreadDms, ...readDms], groups: [...unreadGroups, ...readGroups] };
      }),
    ...queryOpts,
    enabled: opts?.enabled !== false && !!currentUserId && !!streamClient?.user?.id,
    onError: (error, ...args) => {
      logStreamErrorToSentry(error);
      if (queryOpts?.onError) {
        queryOpts.onError(error, ...args);
      }
    },
  });

  return { ...query, queryKey };
};

export const useQueryConversationMessages = (
  streamClient: StreamChat | undefined,
  conversationId: string,
  opts: QueryOptionsWithOnError<InfiniteData<Message[]>> & { aroundMessageId?: string } = {}
) => {
  return useInfiniteQueryWithSetter({
    queryKey: queryKeys.messages(conversationId, opts.aroundMessageId),
    queryFn: ({ pageParam }) =>
      verifyClientWithUser(streamClient).then((client) =>
        getConversationMessages({
          client,
          conversationId,
          cursor: pageParam?.cursor,
          limit: USERS_QUERY_LIMIT,
          direction: pageParam?.direction,
          messageId: opts.aroundMessageId,
        })
      ),
    getNextPageParam: (lastPage) => {
      if (!opts.aroundMessageId) return undefined;
      const hasNextPage = lastPage.length === USERS_QUERY_LIMIT;
      return hasNextPage ? { cursor: lastPage[lastPage.length - 1].id, direction: 'next' } : undefined;
    },
    getPreviousPageParam: (firstPage) => {
      const hasPreviousPage = firstPage.length === USERS_QUERY_LIMIT;
      return hasPreviousPage ? { cursor: firstPage[0].id, direction: 'previous' } : undefined;
    },
    ...opts,
    enabled: opts?.enabled !== false && !!streamClient?.user?.id,
    onError: (error, ...args) => {
      logStreamErrorToSentry(error);
      if (opts?.onError) {
        opts.onError(error, ...args);
      }
    },
  });
};

export const useQueryReplies = (
  streamClient: StreamChat | undefined,
  activeConversationId: string,
  parentId: string,
  opts: QueryOptionsWithOnError<InfiniteData<Message[]>> = {}
) => {
  return useInfiniteQueryWithSetter({
    queryKey: queryKeys.threads(parentId),
    queryFn: ({ pageParam: cursor }) =>
      verifyClientWithUser(streamClient).then((client) =>
        getReplies(client, activeConversationId, parentId, USERS_QUERY_LIMIT, cursor)
      ),
    getPreviousPageParam: (lastPage) => {
      const hasPreviousPage = lastPage.length === USERS_QUERY_LIMIT;

      return hasPreviousPage ? lastPage[0]?.id : undefined;
    },
    ...opts,
    onError: (error, ...args) => {
      logStreamErrorToSentry(error);
      if (opts?.onError) {
        opts.onError(error, ...args);
      }
    },
  });
};

export const useSetReplies = () => {
  type Data = Message[];
  type DataUpdater = (prev: UseInfiniteQueryResult<Data>['data'] | undefined) => UseInfiniteQueryResult<Data>['data'];

  const queryClient = useQueryClient();

  return (threadId: string, updater: DataUpdater) => {
    queryClient.setQueryData<UseInfiniteQueryResult<Data>['data']>(queryKeys.threads(threadId), updater);
  };
};

export const useGetRepliesQueryData = () => {
  const queryClient = useQueryClient();

  return (messageId: string): UseInfiniteQueryResult<Message[]>['data'] | undefined =>
    queryClient.getQueryData(queryKeys.threads(messageId));
};

export const useRemoveRepliesQuery = () => {
  const queryClient = useQueryClient();

  return () => queryClient.removeQueries(threadsBaseKeys);
};

export const useGetThreads = (
  streamClient: StreamChat | undefined,
  orgId: string,
  userId: string,
  opts: ContextlessQueryObserverOptions = {}
) => {
  return useInfiniteQueryWithSetter({
    queryKey: queryKeys.threadsList(orgId),
    queryFn: ({ pageParam: next }) => {
      return verifyClientWithUser(streamClient).then((client) => getThreads(client, THREADS_QUERY_LIMIT, userId, next));
    },
    getNextPageParam: (lastPage) => {
      return lastPage.next;
    },
    ...opts,
    enabled: opts.enabled !== false && !!streamClient && !!userId,
    onError: (error) => {
      logStreamErrorToSentry(error);
    },
  });
};

export const useGetUnreadThreadsCount = (
  streamClient: StreamChat | undefined,
  orgId: string,
  opts: QueryOptionsWithOnError<number> = {}
) => {
  return useQueryWithSetter({
    queryKey: [...queryKeys.threadsList(orgId), 'id-of-unread-count'],
    queryFn: () => verifyClientWithUser(streamClient).then(getThreadsUnreadCount),
    ...opts,
    onError: (error, ...args) => {
      logStreamErrorToSentry(error);
      if (opts?.onError) {
        opts.onError(error, ...args);
      }
    },
    enabled: opts.enabled !== false && !!streamClient?.user?.id,
  });
};

export const useGetUsersFromConversation = (conversationQueryKey: string[], enabled: boolean) => {
  const [usersMap, setUsersMap] = useState(new Map<string, User>());
  const queryClient = useQueryClient();

  const { data: conversationData, isLoading } = useQuery({
    queryKey: conversationQueryKey,
    queryFn: () => queryClient.getQueryData<QueryConversationsResponse>(conversationQueryKey),
    enabled: Boolean(queryClient && conversationQueryKey),
  });

  // As the conversation data changes we only need to update the users map if the users in the conversation changes
  useEffect(() => {
    if (enabled && conversationData) {
      const map = new Map<string, User>();
      const allConversations = [...conversationData.dm, ...conversationData.groups];
      const allMembers = allConversations.flatMap((c) => c.members ?? []);
      allMembers.forEach((user) => {
        if (user?.userID && !map.has(user.userID)) {
          map.set(user.userID, user);
        }
      });

      if (!isUsersMapEqual(usersMap, map)) {
        setUsersMap(map);
      }
    }
  }, [conversationData, enabled, usersMap]);

  return { usersMap, remove: () => setUsersMap(new Map<string, User>()), isLoading };
};

/* 
  The following hooks allows to search users by name
*/
export const useGetUserByName = (
  streamClient: StreamChat | undefined,
  orgId: string,
  name: string,
  opts: UseQueryOptions<User[]> = {}
) => {
  return useQuery({
    queryKey: queryKeys.getUsersByName(name),
    queryFn: () =>
      verifyClientWithUser(streamClient).then((client) =>
        getUsersWithParams(client, orgId, { name: { $autocomplete: name } })
      ),
    staleTime: USERS_QUERY_STALE_TIME,
    select: (users) => users.filter((user) => user.userID !== streamClient?.user?.id),
    ...opts,
    onError: (error, ...args) => {
      logStreamErrorToSentry(error);
      if (opts?.onError) {
        opts.onError(error, ...args);
      }
    },
    enabled: opts.enabled !== false && name.length > 0 && !!streamClient?.user?.id,
  });
};

/* 
  The following hooks allows to return a function to search users by name, using a fetchQuery makes it easy to 
  cache it and also works like an async function
*/

export const useGetUserByNameFn = (
  streamClient: StreamChat | undefined,
  orgId: string
): ((name: string) => Promise<User[]>) => {
  const queryClient = useQueryClient();

  return async (name: string) =>
    queryClient
      .fetchQuery({
        queryKey: queryKeys.getUsersByName(name),
        queryFn: () =>
          verifyClientWithUser(streamClient).then((client) =>
            getUsersWithParams(client, orgId, { name: { $autocomplete: name } })
          ),
        staleTime: USERS_QUERY_STALE_TIME,
      })
      .catch((error) => {
        logStreamErrorToSentry(error);
        return [];
      });
};

export const useGetMessagesByKeyword = (
  streamClient: StreamChat | undefined,
  orgId: string,
  query: KeywordSearchQueryParams,
  opts: QueryOptionsWithOnError<InfiniteData<{ rows: Message[]; next: string | undefined }>> & {
    onSettled?: () => void;
  } = {}
) => {
  const queryKey = queryKeys.messagesByKeyword(orgId, query);

  const messageFilters: MessageFilters = {
    ...(query.sender?.length && { user_id: { $in: query.sender } }),
    ...(query.keyword.length && { text: { $autocomplete: query.keyword } }),
    ...(query.attachmentType && { 'attachments.type': query.attachmentType }),
    type: 'regular' as const,
  };
  const channelFilters = {
    ...(query.channels?.length && { id: { $in: query.channels } }),
  };

  return useInfiniteQueryWithSetter({
    queryKey,
    queryFn: ({ pageParam: next }) =>
      verifyClientWithUser(streamClient).then((client) =>
        searchMessages({
          client,
          orgId,
          messageFilters,
          channelFilters,
          limit: MESSAGE_SEARCH_LIMIT,
          next,
        })
      ),
    getNextPageParam: (lastPage, _allPages) => {
      return lastPage.rows.length === MESSAGE_SEARCH_LIMIT ? lastPage.next : undefined;
    },
    staleTime: MESSAGE_SEARCH_QUERY_STALE_TIME,
    ...opts,
    onError: (error, ...args) => {
      logStreamErrorToSentry(error);
      if (opts?.onError) {
        opts.onError(error, ...args);
      }
    },
    enabled: opts.enabled !== false && query.keyword.length > 0 && !!streamClient?.user?.id,
  });
};
